import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

const get = (url, params) => api.get(url, { params }).then((r) => r.data);

export const useStats = () => useQuery({ queryKey: ['stats'], queryFn: () => get('/stats') });
export const useCategories = (type) => useQuery({ queryKey: ['categories', type], queryFn: () => get('/categories', { type }) });

export const useListings = (params) => useQuery({ queryKey: ['listings', params], queryFn: () => get('/listings', params) });
export const useListing = (slug) => useQuery({ queryKey: ['listing', slug], queryFn: () => get(`/listings/${slug}`), enabled: !!slug });

export const useResources = (params) => useQuery({ queryKey: ['resources', params], queryFn: () => get('/resources', params) });
export const useResource = (slug) => useQuery({ queryKey: ['resource', slug], queryFn: () => get(`/resources/${slug}`), enabled: !!slug });

export const useThreads = (params) => useQuery({ queryKey: ['threads', params], queryFn: () => get('/threads', params) });
export const useThread = (slug) => useQuery({ queryKey: ['thread', slug], queryFn: () => get(`/threads/${slug}`), enabled: !!slug });

export const useBlog = (params) => useQuery({ queryKey: ['blog', params], queryFn: () => get('/blog', params) });
export const useBlogPost = (slug) => useQuery({ queryKey: ['blogpost', slug], queryFn: () => get(`/blog/${slug}`), enabled: !!slug });

export const useFeed = (params) => useQuery({ queryKey: ['feed', params], queryFn: () => get('/feed', params) });
export const useFeedSidebar = () => useQuery({ queryKey: ['feedsidebar'], queryFn: () => get('/feed/sidebar') });
export const useFeedComments = (postId, enabled) => useQuery({ queryKey: ['feedcomments', postId], queryFn: () => get(`/feed/${postId}/comments`), enabled: !!enabled });
export const useAddFeedComment = (postId) => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (b) => api.post(`/feed/${postId}/comments`, b).then((r) => r.data), onSuccess: () => { qc.invalidateQueries({ queryKey: ['feedcomments', postId] }); qc.invalidateQueries({ queryKey: ['feed'] }); } });
};
export const useUpdateFeed = () => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: ({ id, content }) => api.put(`/feed/${id}`, { content }).then((r) => r.data), onSuccess: () => qc.invalidateQueries({ queryKey: ['feed'] }) });
};
export const useDeleteFeed = () => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id) => api.delete(`/feed/${id}`).then((r) => r.data), onSuccess: () => qc.invalidateQueries({ queryKey: ['feed'] }) });
};
export const useReactThread = (slug) => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (reaction) => api.post(`/threads/${slug}/react`, { reaction }).then((r) => r.data), onSuccess: () => qc.invalidateQueries({ queryKey: ['thread', slug] }) });
};
export const useReactReply = (slug) => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: ({ rid, reaction }) => api.post(`/replies/${rid}/react`, { reaction }).then((r) => r.data), onSuccess: () => qc.invalidateQueries({ queryKey: ['thread', slug] }) });
};
export const useUpdateThread = (slug) => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (b) => api.put(`/threads/${slug}`, b).then((r) => r.data), onSuccess: () => { qc.invalidateQueries({ queryKey: ['thread', slug] }); qc.invalidateQueries({ queryKey: ['threads'] }); } });
};
export const useDeleteThread = () => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (slug) => api.delete(`/threads/${slug}`).then((r) => r.data), onSuccess: () => qc.invalidateQueries({ queryKey: ['threads'] }) });
};
export const useRankings = () => useQuery({ queryKey: ['rankings'], queryFn: () => get('/rankings') });
export const useStreams = () => useQuery({ queryKey: ['streams'], queryFn: () => get('/streams') });
export const useWallet = (enabled = true) => useQuery({ queryKey: ['wallet'], queryFn: () => get('/wallet'), enabled });
export const useProfile = (username) => useQuery({ queryKey: ['profile', username], queryFn: () => get(`/users/${username}`), enabled: !!username });
const SETTINGS_LS = 'nuyed_public_settings';
const cachedSettings = () => { try { return JSON.parse(localStorage.getItem(SETTINGS_LS)) || undefined; } catch { return undefined; } };
export const usePublicSettings = () => useQuery({
  queryKey: ['settings'],
  queryFn: async () => {
    const d = await get('/settings');
    try { localStorage.setItem(SETTINGS_LS, JSON.stringify(d)); } catch {}
    return d;
  },
  initialData: cachedSettings,
  initialDataUpdatedAt: 0,
  staleTime: 30000,
});
export const usePaymentMethods = () => useQuery({ queryKey: ['paymethods'], queryFn: () => get('/payments/methods') });
export const useAffiliate = (enabled = true) => useQuery({ queryKey: ['affiliate'], queryFn: () => get('/affiliate'), enabled });

export const useCreateBlog = () => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (b) => api.post('/blog', b).then((r) => r.data), onSuccess: () => qc.invalidateQueries({ queryKey: ['blog'] }) });
};

export const uploadFile = async (file, onProgress) => {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress: (e) => onProgress?.(e.total ? Math.round((e.loaded / e.total) * 100) : 50) });
  return data; // {url, path, filename}
};

export const useCreateThread = () => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (b) => api.post('/threads', b).then((r) => r.data), onSuccess: () => qc.invalidateQueries({ queryKey: ['threads'] }) });
};
export const useCreateReply = (slug) => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (b) => api.post(`/threads/${slug}/replies`, b).then((r) => r.data), onSuccess: () => qc.invalidateQueries({ queryKey: ['thread', slug] }) });
};
export const useCreateListing = () => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (b) => api.post('/listings', b).then((r) => r.data), onSuccess: () => qc.invalidateQueries({ queryKey: ['listings'] }) });
};
export const useCreateResource = () => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (b) => api.post('/resources', b).then((r) => r.data), onSuccess: () => qc.invalidateQueries({ queryKey: ['resources'] }) });
};
export const useCreateFeed = () => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (b) => api.post('/feed', b).then((r) => r.data), onSuccess: () => qc.invalidateQueries({ queryKey: ['feed'] }) });
};
export const useLikeFeed = () => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id) => api.post(`/feed/${id}/like`).then((r) => r.data), onSuccess: () => qc.invalidateQueries({ queryKey: ['feed'] }) });
};
